import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { TableListRow, Typography, TableListActions, WithInfoIcon, Row } from 'seagull';
import { Eye } from 'seagull/icons';
import { getStatusLabel, getPaymentDetailTooltip, formatPaymentDetailsList, invoiceLogicTextsPath } from '~/business-logic/invoiceLogic';
import { fromInvoice } from '~/business-logic/paymentMethodCellLogic';
import { InvoiceActionsMenuItems } from '~/components/InvoiceActions/InvoiceActionsMenuItems';
import { displayStatusToSkin, InvoiceNumberCell } from '~/components/InvoiceRow/InvoiceRowCells';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { invoiceSummaryModel, timeModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import styles from './InvoicesTable.module.scss';
function getPaymentMethodText(state, labels) {
    const typeLabels = {
        ach: labels.ach,
        creditCard: labels.creditCard
    };
    switch(state.kind){
        case 'set':
            return "".concat(typeLabels[state.type], " (").concat(state.last4, ")");
        case 'pendingDetails':
            return labels.pendingDetails;
        case 'unverified':
        case 'notSet':
            return labels.notSet;
    }
}
function InvoicesTableRow(props) {
    const { t: tableRowTexts } = useLocaleKeys('invoice.tableRow');
    const { t: invoiceTexts } = useLocaleKeys('invoice');
    const { t: invoiceLogicTexts } = useLocaleKeys(invoiceLogicTextsPath);
    const { t: paymentMethodTexts } = useLocaleKeys('common.paymentMethod');
    const { invoice, hideDueDate, AD_HOC_INVOICING, to, retry } = props;
    const history = useHistory();
    const isVendor = useStore(selectors.user.isVendor);
    const testId = "invoice-row";
    const isAdHoc = AD_HOC_INVOICING && invoice.isAdHoc;
    const paymentDetailTooltip = getPaymentDetailTooltip(invoice.paymentDetails, invoiceLogicTexts.paymentDetailTooltips);
    const paymentMethodCellState = fromInvoice({
        paymentMethodDisplay: invoice.paymentMethodDisplay,
        paymentDetails: invoice.paymentDetails
    });
    const paymentMethodLabels = {
        notSet: invoiceTexts.paymentMethodDisplay.notSet(),
        ach: invoiceTexts.paymentMethodDisplay.ach(),
        creditCard: invoiceTexts.paymentMethodDisplay.creditCard(),
        pendingDetails: paymentMethodTexts.pendingDetails()
    };
    return /*#__PURE__*/ _jsx(TableListRow, {
        as: Link,
        to: to,
        size: "large",
        testId: testId,
        "data-heapid": "invoice",
        items: compact([
            {
                className: styles.invoiceIdCell,
                value: /*#__PURE__*/ _jsx(InvoiceNumberCell, {
                    invoice: invoice,
                    testId: testId,
                    numberSkin: "secondary",
                    numberClassName: styles.invoiceId
                })
            },
            {
                value: /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            ellipsis: true,
                            weight: "normal",
                            testId: "".concat(testId, "-comp-name"),
                            children: [
                                invoiceSummaryModel.otherPartyCompanyName(invoice, isVendor),
                                "\xa0"
                            ]
                        }),
                        isAdHoc && /*#__PURE__*/ _jsx(WithInfoIcon, {
                            testId: "".concat(testId, "-ad-hoc-info"),
                            infoTestId: "".concat(testId, "-ad-hoc-info-icon"),
                            info: invoiceTexts.adHocTooltip(),
                            showInfo: true
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: displayStatusToSkin[invoiceSummaryModel.displayStatus(invoice)],
                    weight: "normal",
                    testId: "".concat(testId, "-status"),
                    children: getStatusLabel(invoiceSummaryModel.displayStatus(invoice), invoiceLogicTexts.displayedStatusLabels)
                })
            },
            {
                value: invoice.paymentDetails.length > 0 && /*#__PURE__*/ _jsx(WithInfoIcon, {
                    className: styles.paymentDetailsCell,
                    info: paymentDetailTooltip,
                    showInfo: !!paymentDetailTooltip,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        ellipsis: true,
                        testId: "".concat(testId, "-payment-details"),
                        children: formatPaymentDetailsList(invoice.paymentDetails, invoiceLogicTexts.paymentDetailLabels, invoiceLogicTexts.labelOnDate)
                    })
                })
            },
            AD_HOC_INVOICING && {
                value: paymentMethodCellState.kind === 'pendingDetails' ? /*#__PURE__*/ _jsx(WithInfoIcon, {
                    info: paymentMethodTexts.pendingDetailsTooltip(),
                    infoTestId: "".concat(testId, "-payment-method-pending-info"),
                    testId: "".concat(testId, "-payment-method-info-wrapper"),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        skin: "secondary",
                        testId: "".concat(testId, "-payment-method"),
                        ellipsis: true,
                        children: getPaymentMethodText(paymentMethodCellState, paymentMethodLabels)
                    })
                }) : /*#__PURE__*/ _jsx(Typography, {
                    skin: paymentMethodCellState.kind === 'set' ? undefined : 'secondary',
                    testId: "".concat(testId, "-payment-method"),
                    ellipsis: true,
                    children: getPaymentMethodText(paymentMethodCellState, paymentMethodLabels)
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    testId: "".concat(testId, "-issued-on"),
                    children: timeModel.formatDateLong(invoice.rawIssuedOn.getTime())
                })
            },
            !hideDueDate && {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    testId: "".concat(testId, "-due-date"),
                    children: timeModel.formatDateLong(invoice.rawDueDate.getTime())
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    weight: "light",
                    skin: "secondary",
                    testId: "".concat(testId, "-amount"),
                    ellipsis: true,
                    children: invoice.totalAmount
                })
            }
        ]),
        actionColumn: /*#__PURE__*/ _jsx(TableListActions, {
            buttonText: tableRowTexts.view(),
            buttonIcon: /*#__PURE__*/ _jsx(Eye, {}),
            onClickButton: ()=>history.push(to),
            testId: testId,
            btnHeapId: "invoice-view-btn",
            menuHeapId: "invoice-menu-btn",
            mobile: {
                title: invoice.formattedNumber,
                subtitle: invoiceSummaryModel.otherPartyCompanyName(invoice, isVendor)
            },
            children: /*#__PURE__*/ _jsx(InvoiceActionsMenuItems, {
                invoice: invoice,
                customerId: null,
                testId: testId,
                heapId: "invoice-menu",
                onRefreshOverride: retry
            })
        })
    }, invoice.id);
}
export default /*#__PURE__*/ memo(InvoicesTableRow);
